import { PureComponent } from 'react'
import { compose } from 'recompose'
import _ from 'lodash'
import moment from 'moment'
import { createI13nNode } from '@vzmi/ec-react-i13n'
import { CDN } from '@vzmi/shp_shared_compo/dist/constants/cdn.js'
import { Helmet } from 'react-helmet'
import HOC from '../../hoc/loading'
import styles from './ExpiringItems.css'
import { pullLeft } from '../../styles/shared.css'

import { withI13nWrapper } from '../Common/I13nWrapper'
import ExportButton from '../Common/ExportButton'
import Popover from '../Common/Popover'
import YTable from '../Common/YTable'
import Information from '../Common/Information'
import { YCard } from '../Common/YCard'
import cx from 'classnames'
import { sendI13nClickEvent } from '../../../lib/i13nUtil'
import { convertDateByMoment, getDefaultTimezoneMoment } from '../../../lib/renderUtil'
import { DEFAULT_PAGINATION, EXPORT_TEMPLATE_SETTING } from '../../constants/globalTypes'
import { YESTERDAY } from '../../constants/date'
import { ExcelExport, ExcelExportHelper, FetchDataUtil } from '@vzmi/component_ec_nodejs_util'
import withConnectedExport from '../../containers/Common/WithConnectedExport'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './ExpiringItems.ga'

/** page title */
const HelmetSetting = () => (
  <Helmet>
    <title>效期警示品項清單</title>
  </Helmet>
)

const availableDaysPopoverContent = (
  <img
    className={styles.availableDaysImg}
    src={`${CDN}/supplier/sell_available_201809.png`}
  />
)

/** 允售天數 Header */
const availableDaysHeader = (
  <Popover
    placement='bottom'
    content={availableDaysPopoverContent}
  >
    允售天數
    <Information className={styles.availableDaysInfo} />
  </Popover>
)

const columns = [
  {
    id: 'index',
    Header: '項次',
    Cell: row => row.page * row.pageSize + row.index + 1,
    width: 60,
    maxWidth: 60
  },
  {
    Header: '商品序號',
    accessor: 'productId',
    width: 90,
    maxWidth: 90
  },
  {
    Header: '商品名稱',
    accessor: 'name',
    className: cx(pullLeft, styles.productName),
    width: 460,
    maxWidth: 460
  },
  {
    id: 'expiryDate',
    Header: '有效期限/到期日',
    accessor: d => convertDateByMoment(moment(new Date(d.expiryDate)), 'YYYY/MM/DD'),
    width: 120,
    maxWidth: 120
  },
  {
    id: 'lastSaleableDate',
    Header: '最後可銷售日期',
    accessor: d => convertDateByMoment(moment(new Date(d.lastSaleableDate)), 'YYYY/MM/DD'),
    width: 120,
    maxWidth: 120
  },
  {
    /* custom header */
    Header: props => availableDaysHeader,
    accessor: 'availableDays'
  },
  {
    Header: '庫存',
    accessor: 'volume'
  }
]

const ExportHelper = new ExcelExportHelper(EXPORT_TEMPLATE_SETTING.expiringItems)

export class ExpiringItems extends PureComponent {
  constructor (props) {
    super(props)
    // 資料統計至前一天
    const periodText = convertDateByMoment(YESTERDAY, 'YYYY/MM/DD')
    this.state = {
      period: YESTERDAY,
      periodText,
      data: [],
      page: 0,
      pages: -1,
      pageSize: DEFAULT_PAGINATION.perPage,
      allowExport: false,
      resultsTotal: 0
    }
  }

  onPageSizeChange = (pageSize, page) => {
    // change page size trigger page reset if needed
    this.setState({
      page,
      pageSize
    })
    this.sendPageview({
      type: GA_CLICK_ACTION.SHOW_PAGES
    })
  }

  fetchTableData = ({ period, pageSize, page, offset }) => {
    const payload = {
      sort: '+expiryDate',
      limit: pageSize,
      offset
    }
    const fetchPromise = this.props.actions.fetchExpiringItems(payload)
      .then((res) => {
        const data = _.get(res, 'payload.expiringItems', []).slice(0, pageSize)
        const pagination = _.get(res, 'payload.pagination', { resultsTotal: 0 })
        const { resultsTotal } = pagination
        const postRes = {
          rows: data,
          pages: Math.ceil(resultsTotal / pageSize),
          resultsTotal: pagination.resultsTotal
        }

        return postRes
      })

    /** set up loading state */
    this.props.loading((done) => {
      fetchPromise
        .then(done).catch(done)
    })
    return fetchPromise
  }

  fetchData = (state, instance) => {
    const { period, page, pageSize } = state
    const offset = (page === 0) ? 0 : page * pageSize
    const payload = { period, pageSize, page, offset }
    this.fetchTableData(payload)
      .then(res => {
        const { rows, pages, resultsTotal } = res
        this.setState({
          page,
          data: rows,
          pages,
          resultsTotal,
          allowExport: !!resultsTotal
        })
      })
  }

  exportExcel = () => {
    const {
      pageSize,
      allowExport
    } = this.state
    const { actions, openExportModal, closeExportModal } = this.props

    if (!allowExport) {
      return
    }

    this.sendPageview({
      type: GA_CLICK_ACTION.EXPORT_EXCEL
    })
    openExportModal()

    const payload = {
      sort: '+expiryDate',
      limit: pageSize,
      offset: 0
    }

    FetchDataUtil
      .fetchAllPages(
        actions.fetchExpiringItems,
        {
          payload,
          selector: res => _.get(res, 'expiringItems', [])
        }
      )
      .then((res) => {
        const {
          data
        } = res
        const option = {
          fileName: ExportHelper.getFileName({ nowDate: getDefaultTimezoneMoment() }),
          sheets: [
            {
              name: ExportHelper.getSheetName(),
              headers: ExportHelper.getSheetHeaders(),
              data: ExportHelper.convertDataByHeaders(data)
            }
          ]
        }
        const ExcelExporter = new ExcelExport(option)
        ExcelExporter.download({ bookType: ExportHelper.getBookType() })
        closeExportModal()
      })
  }

  computePagination = () => {
    const {
      page,
      pageSize,
      resultsTotal
    } = this.state

    const totalCount = resultsTotal || 0
    let pageCount = totalCount === 0 ? totalCount : (page + 1) * +pageSize
    pageCount = totalCount < pageCount ? totalCount : pageCount
    return {
      pageCount,
      totalCount
    }
  }

  sendPageview = (action) => {
    const { i13n } = this.props
    const i13nModel = getI13nModel(action)
    sendI13nClickEvent({ i13n, i13nModel })
  }

  onPageChange = () => {
    this.sendPageview({
      type: GA_CLICK_ACTION.PAGINATION
    })
  }

  render () {
    const { periodText, data, pages, allowExport } = this.state
    const {
      pageCount,
      totalCount
    } = this.computePagination()
    const clz = cx(styles.ExpiringItems)
    const pageIndicator = pageCount === 0 ? '--' : `${pageCount} / ${totalCount}`

    return (
      <div className={clz}>
        <HelmetSetting />
        <div className={styles.header}>
          <div className={styles.pageCount}>
            {`共 ${pageIndicator} 筆`}
          </div>
          <ExportButton
            className={styles.exportButton}
            data-role='expiring-items-export-button'
            disabled={!allowExport}
            onClick={() => this.exportExcel()}
          />
          <Information
            className={styles.info}
            description={`資料統計至：${periodText}`}
          />
        </div>
        <YCard className={styles.productList}>
          <YTable
            manual // informs React Table that you'll be handling sorting and pagination server-side
            data={data}
            pages={pages} // Display the total number of pages
            loading={this.props.isLoading} // Display the loading overlay when we need it
            columns={columns}
            onFetchData={this.fetchData} // Request new data when things change
            onPageSizeChange={this.onPageSizeChange}
            onPageChange={this.onPageChange}
          />
        </YCard>
      </div>
    )
  }
}

const hoc = compose(
  withConnectedExport,
  createI13nNode,
  HOC,
  withI13nWrapper
)

export default hoc(ExpiringItems)
